import type {
  CoachingSessionStatus,
  CoachingSessionType,
} from '@efdigital/ll-addon-data';
import type { SearchSessionsState } from '@efdigital/ll-admin-v2/components/search/search-sessions.gts';
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';

const DEFAULT_LIMIT = 50;

function isSessionType(value: string): value is CoachingSessionType {
  return value === 'coaching' || value === 'chemistry';
}

export default class CoachingSessionsIndexController extends Controller {
  queryParams = [
    'sessionType',
    'statuses',
    'offset',
    'coachUserId',
    'coacheeUserId',
  ];

  @tracked sessionType: CoachingSessionType | '' = '';
  @tracked statuses: CoachingSessionStatus[] = [];
  @tracked offset = 0;

  /**
   * Scope, set by whoever linked here — a coach's page, a coachee, a
   * partnership (as its coach + coachee, since those never change for a given
   * partnership). Kept as query params so a scoped list stays shareable and
   * the status/type filters still apply on top of it.
   */
  @tracked coachUserId = '';
  @tracked coacheeUserId = '';

  limit = DEFAULT_LIMIT;

  get filters() {
    return {
      ...(this.coachUserId ? { coachUserId: this.coachUserId } : {}),
      ...(this.coacheeUserId ? { coacheeUserId: this.coacheeUserId } : {}),
    };
  }

  serializeSessionType(value: CoachingSessionType | '') {
    return value || undefined;
  }

  deserializeSessionType(value: string) {
    return isSessionType(value) ? value : '';
  }

  serializeStatuses(value: CoachingSessionStatus[]) {
    return value.length > 0 ? value.join(',') : undefined;
  }

  deserializeStatuses(value: string | string[]) {
    const raw = Array.isArray(value) ? value.join(',') : value;
    if (!raw) {
      return [];
    }
    return raw.split(',').filter(Boolean) as CoachingSessionStatus[];
  }

  serializeOffset(value: number) {
    return value > 0 ? String(value) : undefined;
  }

  deserializeOffset(value: string) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }

  setState = (state: Partial<SearchSessionsState>) => {
    if (state.sessionType !== undefined) {
      this.sessionType = state.sessionType;
    }
    if (state.statuses !== undefined) {
      this.statuses = state.statuses;
    }
    if (state.offset !== undefined) {
      this.offset = state.offset;
    }
    if (state.coachUserId !== undefined) {
      this.coachUserId = state.coachUserId;
    }
    if (state.coacheeUserId !== undefined) {
      this.coacheeUserId = state.coacheeUserId;
    }
  };

  setOffset = (offset: number) => {
    this.offset = offset;
  };
}
